/**
 * @file AiGalleryPanel — Agent 会话内 AI Gallery HTML：编排布局与数据源
 * 
 * 功能流程：
 * 1. 用户点击包含 cos_url 的卡片
 * 2. 面板自动将 cos_url 转换为代理 URL（新接口：/api/v1/util/cos/:cos_type/*file_path）
 * 3. 代理 URL 用于 iframe 预览（后端自动处理鉴权和转发）
 * 4. 同时下载 HTML 内容用于代码展示
 */
import React, { useRef } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { useUrlQueryParam } from '@/pages/aigc/hooks/useQuery';
import { ReportStepTabs } from '../../../components/ReportViewer/ReportStepTabs';
import type { TReportStepType } from '../../../components/ReportViewer/types';
import type { ChatDSTemplateActionsListProps } from '../../type';
import { AI_GALLERY_STEP, getGalleryStepTabsConfig } from './constants';
import { useAiGalleryPanel } from './hooks/useAiGalleryPanel';
import { AiGalleryToolbar } from './components/AiGalleryToolbar';
import { AiGalleryContentArea } from './components/AiGalleryContentArea';
import { AiGalleryPublishModal } from './components/AiGalleryPublishModal';
import { AiGalleryShareLinkModal } from './components/AiGalleryShareLinkModal';
import './AiGalleryPanel.scss';

export interface IAiGalleryPanelProps extends ChatDSTemplateActionsListProps {
  /** 会话 ID */
  sessionId?: string;
  /** 容器类名（由 ChatDSTemplate 传入） */
  className?: string;
  /** 与魔法海报一致：拖拽标题栏时提升层级 */
  onFocus?: () => void;
  /** 与魔法海报一致：标题栏拖拽 */
  onHeaderMouseDown?: (e: React.MouseEvent) => void;
  /** 是否展示关闭 */
  showCloseButton?: boolean;
  /** 是否显示发布按钮（分享场景下隐藏） */
  showPublish?: boolean;
}

/**
 * AI Gallery HTML 右侧面板
 * 
 * 接收包含 cos_url 的数据，点击卡片后：
 * 1. 自动将 cos_url 转换为代理 URL（通过后端转发 COS 私有桶文件）
 * 2. 第一个页签展示 iframe 预览
 * 3. 第二个页签展示 HTML 代码
 */
export const AiGalleryPanel: React.FC<IAiGalleryPanelProps> = ({
  dsReport,
  handleUpdateDsReport,
  streamingState,
  className,
  onFocus,
  onHeaderMouseDown,
  showCloseButton = true,
  showPublish = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const report = (dsReport || {}) as Record<string, unknown>;

  const [, setTemplateParam] = useUrlQueryParam(['template']);

  const {
    stepActive,
    setStepActive,
    previewUrl,
    htmlSource,
    displayTitle,
    toolbarDisabled,
    isLoading,
    handleDownload,
    handleCopyClipboard,
    showCopyTips,
    handlePublish,
    publishModalVisible,
    closePublishModal,
    handleConfirmPublish,
    shareLinkModalVisible,
    publishedUrl,
    handlePublishSuccess,
    closeShareLinkModal,
  } = useAiGalleryPanel({
    dsReport: report,
    streamingState,
  });

  const galleryStepTabsConfig = getGalleryStepTabsConfig(stepActive);

  /**
   * 切换预览 / 代码步骤
   */
  const handleStepChange = (step: TReportStepType): void => {
    setStepActive(
      step as typeof AI_GALLERY_STEP.PREVIEW | typeof AI_GALLERY_STEP.CODE,
    );
  };

  /**
   * 关闭右侧面板
   */
  const handleClose = (): void => {
    // 清除 URL 中的 template=splitView 参数
    const currentTemplate = new URLSearchParams(window.location.search).get('template');
    if (currentTemplate === 'splitView') {
      setTemplateParam({ template: undefined });
    }
    handleUpdateDsReport?.({ show: false });
  };

  const isPreviewStep = stepActive === AI_GALLERY_STEP.PREVIEW;
  const isCodeStep = stepActive === AI_GALLERY_STEP.CODE;
  const canCopy =
    Boolean((htmlSource || '').trim()) || Boolean((previewUrl || '').trim());

  /**
   * 全屏：在新页面打开当前 HTML 报告
   */
  const handleFullscreen = (): void => {
    const url = (previewUrl || '').trim();
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const panel = (
    <div
      ref={containerRef}
      className={classNames('dba-ai-gallery-panel', className)}
      onMouseDown={onFocus}
      onClick={onFocus}
    >
      <div
        className={classNames('dba-ai-gallery-panel__header', {
          'dba-ai-gallery-panel__header--draggable': !!onHeaderMouseDown,
        })}
        onMouseDown={(e) => {
          onFocus?.();
          onHeaderMouseDown?.(e);
        }}
      >
        <ReportStepTabs
          className={classNames(
            'dba-ai-gallery-panel__step-tabs',
            'dba-report-step-tabs--ai-gallery',
          )}
          activeStep={stepActive}
          isReportDisabled={false}
          onStepChange={handleStepChange}
          onClose={handleClose}
          showCloseButton={showCloseButton}
          stepsConfig={galleryStepTabsConfig}
          disabledTip=""
          disabledTipEn=""
        />

        <AiGalleryToolbar
          displayTitle={displayTitle}
          toolbarDisabled={toolbarDisabled}
          canCopy={canCopy}
          isCodeStep={isCodeStep}
          showCopyTips={showCopyTips}
          showPublish={showPublish}
          canFullscreen={Boolean((previewUrl || '').trim())}
          onCopy={() => void handleCopyClipboard()}
          onDownload={handleDownload}
          onPublish={handlePublish}
          onFullscreen={handleFullscreen}
        />
      </div>

      <AiGalleryContentArea
        isPreviewStep={isPreviewStep}
        previewUrl={previewUrl}
        htmlSource={htmlSource}
        isLoading={isLoading}
      />
    </div>
  );

  // Portal 到 body：避免落在分栏/报告浮窗 transform/overflow 层叠上下文内被遮挡
  const galleryModals = (
    <>
      <AiGalleryPublishModal
        visible={publishModalVisible}
        defaultNameCn={displayTitle}
        cosSignedUrl={previewUrl}
        onClose={closePublishModal}
        onConfirm={handleConfirmPublish}
        onPublishSuccess={handlePublishSuccess}
      />
      <AiGalleryShareLinkModal
        visible={shareLinkModalVisible}
        url={publishedUrl}
        onClose={closeShareLinkModal}
      />
    </>
  );

  return (
    <>
      {panel}
      {ReactDOM.createPortal(galleryModals, document.body)}
    </>
  );
};
